import React from 'react';
import Layout from '@/components/Layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, Palette, Home, Shield, Award } from 'lucide-react';

const DesignYourRoof: React.FC = () => {
  const handleIKOClick = () => {
    window.open('https://www.iko.com/na/roofviewer/', '_blank');
  };

  const handleAtlasClick = () => {
    window.open('https://atlas.renoworks.com/pages/project/automasker', '_blank');
  };

  const roofDesignOptions = [
    {
      id: 'iko',
      name: 'IKO RoofViewer',
      tagline: 'Setting the Standard',
      logo: '/lovable-uploads/28d1d6c1-6b9c-4f90-b677-903c6f27a2d8.png',
      description: 'Visualize your roof with IKO\'s premium shingle collection. See how different colors and styles will look on your home.',
      features: [
        'Premium shingle visualizer',
        'Lifetime warranty options',
        'Storm-resistant materials',
        'Professional installation network'
      ],
      onClick: handleIKOClick,
      bgGradient: 'from-red-50 to-orange-50',
      borderColor: 'border-red-200'
    },
    {
      id: 'atlas',
      name: 'Atlas Designer Shingles',
      tagline: 'ScotchGard Protection',
      logo: '/lovable-uploads/4616bbe4-2a20-4bf5-870b-6a4e5f6e3056.png',
      description: 'Experience Atlas Designer Shingles with advanced ScotchGard protection. Premium roofing with superior algae resistance.',
      features: [
        'Designer shingle collection',
        'ScotchGard algae protection',
        'Enhanced curb appeal',
        'Long-lasting performance'
      ],
      onClick: handleAtlasClick,
      bgGradient: 'from-blue-50 to-indigo-50',
      borderColor: 'border-blue-200'
    }
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <Badge variant="secondary" className="mb-6 px-4 py-2 text-sm font-medium">
              <Palette className="w-4 h-4 mr-2" />
              Interactive Roof Design Tools
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Design Your
              <span className="text-brand-trophy-gold block mt-2">Perfect Roof</span>
            </h1>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Visualize your dream roof before installation. Use our partner tools to see exactly how premium shingles will transform your home's appearance.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <div className="flex items-center text-gray-600">
                <Home className="w-5 h-5 mr-2 text-brand-trophy-gold" />
                Real-time visualization
              </div>
              <div className="flex items-center text-gray-600">
                <Shield className="w-5 h-5 mr-2 text-brand-trophy-gold" />
                Premium materials only
              </div>
              <div className="flex items-center text-gray-600">
                <Award className="w-5 h-5 mr-2 text-brand-trophy-gold" />
                Professional installation
              </div>
            </div>
          </div>

          {/* Hero Image */}
          <div className="max-w-5xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="/lovable-uploads/e669f444-8e0c-418d-aae8-0d2ad9907f1c.png" 
                alt="Beautiful home with premium roof design visualization"
                className="w-full h-auto"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            </div>
          </div>
        </section>

        {/* Design Options */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Choose Your Design Platform
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Both platforms offer premium visualization tools backed by industry-leading manufacturers. Start designing your roof today.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {roofDesignOptions.map((option) => (
                <Card key={option.id} className={`relative overflow-hidden border-2 ${option.borderColor} hover:shadow-xl transition-all duration-300 group`}>
                  <div className={`absolute inset-0 bg-gradient-to-br ${option.bgGradient} opacity-50`} />
                  
                  <CardHeader className="relative pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <img 
                        src={option.logo} 
                        alt={`${option.name} logo`}
                        className="h-16 w-auto object-contain"
                      />
                      <Badge variant="outline" className="bg-white/80">
                        Premium Partner
                      </Badge>
                    </div>
                    
                    <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                      {option.name}
                    </CardTitle>
                    <CardDescription className="text-sm font-medium text-gray-600 mb-4">
                      {option.tagline}
                    </CardDescription>
                    <p className="text-gray-700 text-base leading-relaxed">
                      {option.description}
                    </p>
                  </CardHeader>

                  <CardContent className="relative pt-4">
                    <div className="space-y-3 mb-6">
                      {option.features.map((feature, index) => (
                        <div key={index} className="flex items-center text-gray-700">
                          <div className="w-2 h-2 bg-brand-trophy-gold rounded-full mr-3 flex-shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Button 
                      onClick={option.onClick}
                      className="w-full bg-brand-charcoal-gray hover:bg-brand-charcoal-gray/90 text-white font-semibold py-3 group-hover:scale-105 transition-all duration-300"
                      size="lg"
                    >
                      Start Designing Your Roof
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-brand-charcoal-gray">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-bold text-white mb-4">
              Ready to Install Your Dream Roof?
            </h3>
            <p className="text-xl text-gray-300 mb-8">
              Once you've designed your perfect roof, get a free estimate from Eastern NC's premier roofing contractor.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => window.location.href = '/contact'}
                size="lg"
                className="bg-brand-trophy-gold hover:bg-brand-trophy-gold/90 text-brand-charcoal-gray font-bold px-8 py-3"
              >
                Get Free Estimate
              </Button>
              <Button 
                onClick={() => window.location.href = '/book-inspection'}
                variant="outline"
                size="lg"
                className="border-white text-black hover:bg-white hover:text-brand-charcoal-gray font-semibold px-8 py-3"
              >
                Schedule Inspection
              </Button>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default DesignYourRoof;